/**
 * Keeps the dashboard current without anyone pressing refresh.
 *
 * The server streams "scope X changed" over /api/stream; this marks the
 * matching React Query caches stale, and React Query refetches the ones that
 * are actually on screen. Nothing else in the app changes: a page keeps using
 * useQuery exactly as before and simply receives fresh data.
 *
 * Events carry no job or customer data — only a scope name — so the refetch
 * goes through the normal API with the user's own session and permissions.
 */

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';

const RealtimeContext = createContext({ status: 'connecting', lastEventAt: null });

export const useRealtime = () => useContext(RealtimeContext);

/**
 * Which caches a scope touches. Keys are matched by their first element, so
 * ['booked-slots', date, location] is covered by 'booked-slots'.
 *
 * A scope that isn't listed refreshes everything on screen rather than
 * silently updating nothing — being too eager is cheap, missing a change is not.
 */
const SCOPES = {
  // Anything that moved in Zuper: schedules, statuses, assignments.
  jobs: ['live-jobs', 'unbooked-jobs', 'calendar', 'booked-slots', 'double-bookings',
    'daily-report', 'search-jobs', 'dashboard-stats', 'stats', 'date-availability',
    'reminders', 'campaigns', 'current-campaign', 'scheduled-reminders', 'week-info'],
  // A booking was confirmed, moved or cancelled — by us or by staff.
  bookings: ['live-jobs', 'unbooked-jobs', 'calendar', 'booked-slots', 'double-bookings',
    'daily-report', 'dashboard-stats', 'stats', 'date-availability', 'booking-logs',
    'booking-activity', 'analytics', 'reminders', 'campaigns', 'current-campaign', 'week-info'],
  reminders: ['reminders', 'campaigns', 'current-campaign', 'scheduled-reminders', 'alerts', 'alert-logs'],
  dayoffs: ['day-offs', 'booked-slots', 'date-availability', 'mechanics'],
  settings: ['app-settings', 'filter-settings', 'all-locations', 'admin-locations'],
};

export default function RealtimeProvider({ children }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [status, setStatus] = useState('connecting');
  const [lastEventAt, setLastEventAt] = useState(null);
  // Several changes often land together (a booking writes a job, its children
  // and a reminder). Collect the scopes for a moment, then refetch once.
  const pending = useRef(new Set());
  const flushTimer = useRef(null);

  useEffect(() => {
    // The stream is authenticated. On the login screen and the public booking
    // pages there is no session, and connecting would just retry a 401.
    if (!user) { setStatus('offline'); return undefined; }
    let source;
    let closed = false;

    const flush = () => {
      flushTimer.current = null;
      const scopes = [...pending.current];
      pending.current.clear();
      if (scopes.length === 0) return;

      const unknown = scopes.filter((s) => !SCOPES[s]);
      if (unknown.length > 0) {
        queryClient.invalidateQueries();
        return;
      }
      const keys = new Set(scopes.flatMap((s) => SCOPES[s]));
      for (const key of keys) queryClient.invalidateQueries({ queryKey: [key] });
    };

    const connect = () => {
      if (closed) return;
      source = new EventSource('/api/stream', { withCredentials: true });

      source.addEventListener('hello', () => setStatus('live'));
      source.addEventListener('change', (event) => {
        let scope = 'unknown';
        try { scope = JSON.parse(event.data).scope || 'unknown'; } catch { /* refresh everything */ }
        pending.current.add(scope);
        setLastEventAt(new Date());
        if (!flushTimer.current) flushTimer.current = setTimeout(flush, 400);
      });
      source.onopen = () => setStatus('live');
      source.onerror = () => {
        // EventSource reconnects by itself; show the gap while it does.
        setStatus(source && source.readyState === 2 ? 'offline' : 'reconnecting');
      };
    };

    connect();
    // Coming back to the tab after a sleep: refetch at once rather than waiting
    // for the next change.
    const onVisible = () => {
      if (document.visibilityState === 'visible') queryClient.invalidateQueries();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      closed = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (flushTimer.current) clearTimeout(flushTimer.current);
      if (source) source.close();
    };
  }, [queryClient, user]);

  const value = useMemo(() => ({ status, lastEventAt }), [status, lastEventAt]);
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}
